import { useCallback, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import UserContext from "../context/UserContext";
import customersService from "../services/customersService";
import CustomSpinner from "../components/CustomSpinner";

export default function Customers() {
  const { user, loading, setLoading } = useContext(UserContext);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ name: "" });
  const [notice, setNotice] = useState("");

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await customersService.getCustomers();
      setCustomers(response.data);
    } catch (error) {
      setNotice(error.response?.data?.message || "No se pudieron cargar los clientes.");
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => { if (user) loadCustomers(); }, [user, loadCustomers]);

  const submit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      await customersService.createCustomer(form);
      setForm({ name: "" });
      setNotice("Cliente registrado correctamente.");
      await loadCustomers();
    } catch (error) {
      setNotice(error.response?.data?.message || "No se pudo registrar el cliente.");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este cliente?")) return;
    try {
      setLoading(true);
      await customersService.deleteCustomer(id);
      setCustomers((current) => current.filter((customer) => customer.id !== id));
    } catch (error) {
      setNotice(error.response?.data?.message || "No se pudo eliminar el cliente.");
    } finally {
      setLoading(false);
    }
  };

  const registerVisit = async (id) => {
    try {
      setLoading(true);
      const response = await customersService.registerVisit(id);
      setCustomers((current) => current.map((customer) => customer.id === id ? { ...customer, visits: response.data.visits } : customer));
      setNotice("Visita registrada correctamente.");
    } catch (error) {
      setNotice(error.response?.data?.message || "No se pudo registrar la visita.");
    } finally {
      setLoading(false);
    }
  };

  const removeVisit = async (customer) => {
    if (!customer.visits || !window.confirm(`¿Restar la última visita de ${customer.name}?`)) return;
    try {
      setLoading(true);
      const response = await customersService.removeVisit(customer.id);
      setCustomers((current) => current.map((item) => item.id === customer.id ? { ...item, visits: response.data.visits } : item));
      setNotice("Visita restada correctamente.");
    } catch (error) {
      setNotice(error.response?.data?.message || "No se pudo restar la visita.");
    } finally {
      setLoading(false);
    }
  };

  const copyCustomerLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/cliente/${slug}`);
      setNotice("Link copiado al portapapeles.");
    } catch {
      setNotice("No se pudo copiar el link.");
    }
  };

  if (!user) return <Navigate to="/" replace />;

  return (
    <>
      {loading && <CustomSpinner />}
      <main className="min-h-[91vh] banner-1 px-4 py-6 text-slate-900 dark:text-slate-100 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-3xl border border-black/10 bg-slate-50/80 p-4 shadow-2xl dark:border-white/10 dark:bg-slate-900/80 sm:p-6">
          <h1 className="text-2xl font-semibold">Clientes</h1>
          <p className="mb-6 mt-1 text-sm text-slate-500">Crea y administra tus clientes.</p>
          {notice && <div className="mb-5 rounded-xl bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700">{notice}</div>}
          <form onSubmit={submit} className="mb-6 grid gap-3 rounded-2xl border border-black/10 p-4 dark:border-white/10 md:grid-cols-[1fr_auto]">
            <input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Nombre completo" className="rounded-xl border border-slate-300 bg-transparent px-3 py-2.5 dark:border-slate-700" />
            <button className="rounded-xl border border-sky-500/30 bg-sky-500/10 px-5 py-2.5 text-sm font-medium text-sky-700">Agregar cliente</button>
          </form>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {customers.map((customer) => (
              <article key={customer.id}
                className="overflow-hidden rounded-xl border bg-white text-slate-900 shadow-sm 
              dark:bg-slate-800 dark:text-slate-100">
                <header className="flex min-h-14 items-center justify-end border-b 
                border-slate-200 px-3 py-2 dark:border-slate-700">
                  <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => registerVisit(customer.id)}
                    title={`Registrar visita de ${customer.name}`}
                    aria-label={`Registrar visita de ${customer.name}`}
                    className="rounded-md border border-emerald-500 px-2 py-1.5 text-emerald-600 transition-colors hover:bg-emerald-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 dark:focus:ring-offset-slate-800"
                  >
                    <span aria-hidden="true">+</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeVisit(customer)}
                    title={`Restar última visita de ${customer.name}`}
                    aria-label={`Restar última visita de ${customer.name}`}
                    className="rounded-md border border-amber-500 px-2 py-1.5 text-amber-600 transition-colors hover:bg-amber-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 dark:focus:ring-offset-slate-800"
                  >
                    <span aria-hidden="true">−</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => copyCustomerLink(customer.slug)}
                    title={`Copiar link de ${customer.name}`}
                    aria-label={`Copiar link de ${customer.name}`}
                    className="rounded-md border border-sky-500 px-2 py-1.5 text-sky-600 transition-colors hover:bg-sky-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 dark:focus:ring-offset-slate-800"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <rect x="9" y="9" width="11" height="11" rx="2" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(customer.id)}
                    title={`Eliminar a ${customer.name}`}
                    aria-label={`Eliminar a ${customer.name}`}
                    className="rounded-md border border-rose-500 px-2 py-1.5 text-rose-600 transition-colors hover:bg-rose-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-2 dark:focus:ring-offset-slate-800"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18m-2 0-.75 14.25a2 2 0 0 1-2 1.75H7.75a2 2 0 0 1-2-1.75L5 6m3 0V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m-7 4v7m4-7v7" />
                    </svg>
                  </button>
                  </div>
                </header>
                <div className="p-4">
                  <h2 className="font-medium">{customer.name}</h2>
                  <p className="mt-1 text-sm text-slate-500">{customer.visits || 0} visitas registradas</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>
    </>
  );
}
